import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import PipelineProjectService, {
  type ProjectPhase,
} from '@frontend/AppSettings/services/PipelineProjectService';
import { todayUtcMs } from './phaseGanttModel';
import { anyImminent, buildReadiness } from './phaseReadinessModel';

/**
 * The readiness strip — Build R, the plan-family's LAST piece and its one sanctioned advisory.
 *
 * It lives HERE, beside the Gantt, because readiness is about dates; the Team-tab panels stay
 * assignment surfaces and never colour (their no-badge pins re-run untouched). A phase earns a
 * line only when IMMINENT (effective start within 7 days, or running) and still short of a
 * planned slot half — "Ditch · labour 5 of 9 · equipment 2 of 4" — and the line disappears when
 * filled or when the phase completes. A phase 30 days out with nobody named renders NOTHING, and
 * with nothing imminent the crew/equipment reads are never even issued (at-rest silence is a
 * network promise too).
 *
 * Unit double-booking across genuinely overlapping effective windows is disclosed here and
 * nowhere else — and only while the project is LIVE: the whole strip (fetch included) rides the
 * imminence gate, so a fully-complete project's historical overlap shows nowhere. That is a
 * judgment call (review L2): readiness is about what is ABOUT to matter, and the network-silence
 * promise outranks a retrospective disclosure no one can act on. DISPLAY ONLY: no store, no
 * button, no form — nothing here blocks any write.
 */
interface PhaseReadinessStripProps {
  matterid: string;
  phases: ProjectPhase[];
}

const PhaseReadinessStrip = ({ matterid, phases }: PhaseReadinessStripProps): JSX.Element | null => {
  const todayMs = todayUtcMs();
  const imminent = anyImminent(phases, todayMs);

  // The Team panels' own query keys, shared verbatim — a near-miss key doubles the fetch and
  // lets two copies of one roster disagree mid-refetch (the tab's standing rule).
  const crew = useQuery({
    queryKey: ['ProjectPhaseCrew', matterid],
    queryFn: () => PipelineProjectService.getPhaseCrew(matterid),
    enabled: imminent,
  });
  const equipment = useQuery({
    queryKey: ['ProjectPhaseEquipment', matterid],
    queryFn: () => PipelineProjectService.getPhaseEquipment(matterid),
    enabled: imminent,
  });

  const model = useMemo(
    () => buildReadiness({ phases, crew: crew.data, equipment: equipment.data, todayMs }),
    [phases, crew.data, equipment.data, todayMs],
  );

  if (!imminent || (model.rows.length === 0 && model.overlaps.length === 0)) return null;

  return (
    <section className="pipeline-readiness" aria-label="Readiness">
      <h3 className="pipeline-readiness-title">Readiness</h3>
      {model.rows.map((row) => (
        <p key={row.phaseId} className="pipeline-readiness-item">
          <strong>{row.phaseName}</strong>
          {' · '}
          {row.started ? 'started' : `starts ${row.effectiveStart}`}
          {row.labour && ` · labour ${row.labour.named} of ${row.labour.planned}`}
          {row.equipment && ` · equipment ${row.equipment.named} of ${row.equipment.planned}`}
        </p>
      ))}
      {model.overlaps.map((o) => (
        <p key={o.unitId} className="pipeline-readiness-item">
          <strong>{o.unitLabel}</strong>
          {` · assigned to ${o.phaseNames.join(' and ')} · windows overlap`}
        </p>
      ))}
    </section>
  );
};

export default PhaseReadinessStrip;
